/**
 * Ordinal traversal for keyboard drag. Keyboard has no pointer coordinates, so
 * instead of faking them the keyboard sensor walks the registered droppables in
 * document order and this function picks the neighbour for an arrow press.
 *
 * Clamps at the ends rather than wrapping: wrapping a phase move (lost → intake)
 * is disorienting when a screen reader announces it, and the five lifecycle
 * buckets read as a bounded list, not a carousel. Disabled droppables are
 * skipped so they never become a resting target.
 */

/**
 * @param currentId  the droppable the dragged item currently hovers
 * @param dir        +1 = next in order, -1 = previous
 * @param ordered    droppable ids in document order
 * @param disabled   ids that cannot receive a drop
 * @returns the neighbour id, or `currentId` when clamped / nothing to move to
 */
export function nextDroppableInOrder(
  currentId: string,
  dir: 1 | -1,
  ordered: readonly string[],
  disabled: ReadonlySet<string> = new Set(),
): string {
  const enabled = ordered.filter((id) => !disabled.has(id))
  if (enabled.length === 0) return currentId

  const idx = enabled.indexOf(currentId)
  if (idx === -1) {
    // The current target is unknown or disabled — enter the list from the end
    // we're travelling away from (Down → first, Up → last).
    return dir === 1 ? enabled[0] : enabled[enabled.length - 1]
  }

  const next = idx + dir
  if (next < 0 || next >= enabled.length) return currentId // clamp at the ends
  return enabled[next]
}
